import { catalogue } from './dataCatalogue.js';

const brands = catalogue.map((item) => ({ key: item.key, name: item.brand }));

/** Two or more choices side by side, the one whose key is `chosen` pressed: the brand, or a category's record type. */
function Toggle({ label, items, chosen, onChoose }) {
  return (
    <div className="segmented" role="group" aria-label={label}>
      {items.map((item) => (
        <button key={item.key} type="button" aria-pressed={item.key === chosen} className={item.key === chosen ? 'active' : ''} onClick={() => onChoose(item.key)}>{item.name}</button>
      ))}
    </div>
  );
}

/** A brand's categories split by `section` (BedMaps: bedmaps.com and the BedMaps app), in order; one unnamed part when it has none. */
function sectionsOf(brand) {
  const names = [...new Set(brand.categories.map((item) => item.section))];
  return names.map((name) => ({ name, categories: brand.categories.filter((item) => item.section === name) }));
}

/** A list of categories, each name opening its page, with what it collects beside it. */
function CategoryTable({ label, categories, onChoose }) {
  return (
    <div className="mod-table-wrap">
      <table className="mod-audit data-catalogue" aria-label={label}>
        <thead><tr><th scope="col">Category</th><th scope="col">What we collect</th></tr></thead>
        <tbody>
          {categories.map((item) => (
            <tr key={item.key}>
              <th scope="row"><button type="button" className="admin-link" onClick={() => onChoose(item.key)}>{item.name}</button></th>
              <td>{item.summary}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * The Data tab: what one brand collects, picked with a toggle between Resident Service 360 and BedMaps (`show`). With no `category`
 * it lists the brand's categories (BedMaps' under a heading for each section), each opening its own page. A category's page has a
 * toggle for its record types (`record`, such as residents or trades) and lists the chosen one's groups, one bulleted row per thing
 * we record with an example beside it. The Category dropdown moves between the pages. `onOpen(params)` changes what is shown.
 */
export default function DataTab({ show, category, record, onOpen }) {
  const brand = catalogue.find((item) => item.key === show) || catalogue[0];
  const sections = sectionsOf(brand);
  const open = brand.categories.find((item) => item.key === category);
  const kind = open && (open.records.find((item) => item.key === record) || open.records[0]);
  const choose = (key) => onOpen(key ? { show: brand.key, category: key } : { show: brand.key });
  const options = (categories) => categories.map((item) => <option key={item.key} value={item.key}>{item.name}</option>);
  return (
    <>
      <div className="mod-filters">
        <Toggle label="Brand" items={brands} chosen={brand.key} onChoose={(key) => onOpen({ show: key })}/>
        <label>Category
          <select value={open?.key || ''} onChange={(event) => choose(event.target.value)}>
            <option value="">All categories</option>
            {sections.map((section) => (section.name
              ? <optgroup key={section.name} label={section.name}>{options(section.categories)}</optgroup>
              : options(section.categories)))}
          </select>
        </label>
      </div>
      {open && open.records.length > 1 && (
        <div className="mod-filters">
          <Toggle label="Record type" items={open.records} chosen={kind.key} onChoose={(key) => onOpen({ show: brand.key, category: open.key, record: key })}/>
        </div>
      )}
      {open ? (
        <div className="mod-table-wrap">
          {/* One row per thing we record, so each sits beside its example; the group's name spans its rows. */}
          <table className="mod-audit data-catalogue data-records" aria-label={`What ${brand.brand} records: ${open.name}, ${kind.name}`}>
            <thead><tr><th scope="col">Group</th><th scope="col">What we record</th><th scope="col">Examples</th></tr></thead>
            {kind.groups.map(([group, items]) => (
              <tbody key={group}>
                {items.map(([item, example], index) => (
                  <tr key={item}>
                    {index === 0 && <th scope="rowgroup" rowSpan={items.length}>{group}</th>}
                    <td className="data-point">{item}</td>
                    <td className="data-example">{example}</td>
                  </tr>
                ))}
              </tbody>
            ))}
          </table>
        </div>
      ) : sections.map((section) => (section.name ? (
        <section key={section.name}>
          <h2 className="admin-section">{section.name}</h2>
          <CategoryTable label={`What ${brand.brand} collects: ${section.name}`} categories={section.categories} onChoose={choose}/>
        </section>
      ) : (
        <CategoryTable key="all" label={`What ${brand.brand} collects`} categories={section.categories} onChoose={choose}/>
      )))}
    </>
  );
}
